import { Dispatch, SetStateAction, useCallback, useState } from "react"
import { List, ListItem, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useFormikContext } from "formik"
import { ActionMenu } from "./ActionMenu"
import { DialogStateProps } from "./CreateUpdateSurveyFormContent"
import { CreateOrEditSurveyFormValues, SurveyQuestion } from "../merchantSurveyTypes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"

const useStyles = makeStyles()((theme) => ({
    listItem: {
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
        padding: "0.6rem 1rem 0.6rem 0.75rem",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "1.25rem",
    },
    listItemTitle: {
        color: theme.palette.tailwind.slate[700],
    },
}))

type QuestionsListProps = Readonly<{
    list: SurveyQuestion[]
    setDialogState: Dispatch<SetStateAction<DialogStateProps>>
}>

export const QuestionsList = ({ list, setDialogState }: QuestionsListProps) => {
    const { values, setFieldValue } = useFormikContext<CreateOrEditSurveyFormValues>()
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()

    const handleEdit = useCallback(
        (questionItem: SurveyQuestion) => {
            setDialogState((prev) => ({ show: !prev.show, item: questionItem }))
        },
        [setDialogState],
    )

    const handleDelete = useCallback(
        (questionItem: SurveyQuestion) => {
            const updatedQuestions = [...values.questions]
                .filter((question) => question.id !== questionItem.id)
                .map((question, index) => ({ ...question, id: index + 1 }))
            setFieldValue("questions", updatedQuestions)
        },
        [values.questions, setDialogState],
    )

    return (
        <List>
            {list.map((item) => (
                <ListItem key={`item_${item.id}`} className={classes.listItem}>
                    <Typography variant="body2" className={classes.listItemTitle}>
                        {item.title}
                    </Typography>

                    <ActionMenu
                        item={item}
                        menuItems={[
                            {
                                text: l10n.getString("edit"),
                                onClick: () => handleEdit(item),
                            },
                            {
                                text: l10n.getString("delete"),
                                onClick: () => handleDelete(item),
                            },
                        ]}
                    />
                </ListItem>
            ))}
        </List>
    )
}
